import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import Textarea from 'shared/components/input/Textarea';
import { formatCalendar } from 'shared/utils/dateUtils';
import { getText } from 'shared/utils/Text';

import Button from '../../button/Button';
import CommentDropdown from '../CommentDropdown';
import styles from './index.module.scss';

const maxTextLength = 512;

const makeCommentMap = commentList => {
  const map = {};
  for (const comment of commentList) {
    map[comment.id] = comment.comment;
  }
  return map;
};

const CommentBoard = ({
  collaborators,
  comments,
  addComment,
  updateComment,
  deleteComment,
}) => {
  const [text, setText] = useState('');

  const [tab, setTab] = useState('chat');

  const [dropdownPositionClassName, setDropdownPositionClassName] = useState();

  const [dropdownMessageId, setDropdownMessageId] = useState();
  const [editMessageId, setEditMessageId] = useState();
  const [editedText, setEditedText] = useState('');

  const onTextKeyDown = e => {
    if (e.keyCode === 13) {
      e.preventDefault();

      if (e.shiftKey === false && text.trim().length > 0) {
        postComment();
      }
    }
  };

  const onEditTextKeyDown = e => {
    if (e.keyCode === 13) {
      e.preventDefault();

      if (e.shiftKey === false && editedText.trim().length > 0) {
        postCommentUpdate();
      }
    }
  };

  const postComment = () => {
    addComment(text.trim());
    setText('');
  };

  const postCommentUpdate = () => {
    updateComment(editMessageId, editedText.trim());
    resetEditState();
  };

  const removeComment = id => {
    deleteComment(id ?? editMessageId);
    resetEditState();
  };

  const resetEditState = () => {
    setEditMessageId('');
    setEditedText('');
  };

  const chatComments = useMemo(
    () => comments.filter(comment => comment.commentType === 'UserMessage'),
    [comments],
  );
  const logComments = useMemo(
    () => comments.filter(comment => comment.commentType !== 'UserMessage'),
    [comments],
  );

  const initialCommentMap = useMemo(
    () => makeCommentMap(chatComments),
    [chatComments],
  );
  const [originalCommentMap, setOriginalCommentMap] =
    useState(initialCommentMap);

  const changeTabTo = newTab => {
    if (newTab === 'chat') {
      setOriginalCommentMap(makeCommentMap(chatComments));
    } else if (newTab === 'log') {
      setOriginalCommentMap(makeCommentMap(logComments));
    }
    setTab(newTab);
  };

  const renderedComments = tab === 'chat' ? chatComments : logComments;

  const listContainerRef = useRef(null);

  useEffect(() => {
    listContainerRef.current.scrollTo(0, listContainerRef.current.scrollHeight);
  }, [renderedComments]);

  const editedCommentRef = useRef(null);

  useEffect(() => {
    if (editMessageId && editedCommentRef.current) {
      editedCommentRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [editMessageId]);

  const dropdownCommentRef = useRef(null);

  useEffect(() => {
    if (dropdownMessageId && dropdownCommentRef.current) {
      const dropdownArrowTop = 32;
      const dropdownArrowHeight = 18;
      const dropdownHeight = 72;
      const viewportHeight = listContainerRef.current.offsetHeight;
      const currentScroll = listContainerRef.current.scrollTop;
      const commentTop = dropdownCommentRef.current.offsetTop;
      const spaceUnderArrow =
        currentScroll +
        viewportHeight -
        (commentTop + dropdownArrowTop + dropdownArrowHeight);
      const spaceAboveArrow = commentTop + dropdownArrowTop - currentScroll;
      if (spaceUnderArrow > dropdownHeight) {
        // First choice, if there's space under arrow, render there
        setDropdownPositionClassName(styles.dropdownPositionUnder);
      } else if (spaceAboveArrow > dropdownHeight) {
        // Second best choice, if there's space above arrow, render there
        setDropdownPositionClassName(styles.dropdownPositionAbove);
      } else {
        // When there is no clean under or above option, calculate the least bad option.
        // I.e. which option overshoots the viewport the least.
        const middleExtensionBeyondArrow =
          (dropdownHeight - dropdownArrowHeight) / 2;
        const middleOverShoot =
          Math.max(middleExtensionBeyondArrow - spaceUnderArrow, 0) +
          Math.max(middleExtensionBeyondArrow - spaceAboveArrow, 0);
        const underOverShoot = dropdownHeight - spaceUnderArrow;
        const aboveOverShoot = dropdownHeight - spaceAboveArrow;
        if (
          middleOverShoot < underOverShoot &&
          middleOverShoot < aboveOverShoot
        ) {
          setDropdownPositionClassName(styles.dropdownPositionMiddle);
        } else if (aboveOverShoot < underOverShoot) {
          setDropdownPositionClassName(styles.dropdownPositionAbove);
        } else {
          setDropdownPositionClassName(styles.dropdownPositionUnder);
        }
      }
    }
    // We are done with our calculation and then reset the chosen dropdown
    // This means we don't have to listen to any Dropdown onHide to allow
    // the same comment dropdown to be re-measured later
    setDropdownMessageId(null);
  }, [dropdownMessageId]);

  const collaboratorIdMap = useMemo(() => {
    const map = {};
    for (const collaborator of collaborators) {
      map[collaborator.id] = collaborator;
    }
    return map;
  }, [collaborators]);

  return (
    <div className={styles.root}>
      <div className={styles.tabContainer}>
        <Button
          className={classNames(styles.tab, {
            [styles.selected]: tab === 'chat',
          })}
          onClick={() => changeTabTo('chat')}
        >
          {getText('comment-board-tab-chat', chatComments.length)}
        </Button>
        <Button
          className={classNames(styles.tab, {
            [styles.selected]: tab === 'log',
          })}
          onClick={() => changeTabTo('log')}
        >
          {getText('comment-board-tab-log', logComments.length)}
        </Button>
      </div>
      <div className={styles.listContainer} ref={listContainerRef}>
        {renderedComments.map(
          ({ id, userId, comment, created, commentType }) => (
            <div
              key={id}
              className={classNames(styles.comment, {
                [styles.systemMessage]: commentType !== 'UserMessage',
                [styles.newMessage]: originalCommentMap[id] !== comment,
              })}
              ref={
                editMessageId === id
                  ? editedCommentRef
                  : dropdownMessageId === id
                    ? dropdownCommentRef
                    : undefined
              }
            >
              <div className={styles.commentContent}>
                <div className={styles.commentAuthor}>
                  {collaboratorIdMap[userId]
                    ? collaboratorIdMap[userId].alias
                    : getText('comment-board-removed-user')}
                </div>
                {editMessageId === id ? (
                  <div>
                    <Textarea
                      className={styles.textArea}
                      onChange={e => setEditedText(e.target.value)}
                      onKeyDown={onEditTextKeyDown}
                      maxLength={maxTextLength}
                      placeholder={getText('comment-board-input-placeholder')}
                      value={editedText}
                    />
                    <Button
                      className={styles.button}
                      onClick={() =>
                        editedText.trim().length === 0
                          ? removeComment()
                          : postCommentUpdate()
                      }
                    >
                      {editedText.trim().length === 0
                        ? getText('comment-board-delete-comment')
                        : getText('comment-board-update-comment')}
                    </Button>
                    <Button
                      className={classNames(styles.button, styles.secondary)}
                      onClick={resetEditState}
                    >
                      {getText('comment-board-cancel-edit-comment')}
                    </Button>
                  </div>
                ) : (
                  <div className={styles.commentText}>{comment}</div>
                )}
              </div>
              <div className={styles.commentDate}>
                {formatCalendar(created, { showTimeForToday: true })}
              </div>
              {collaboratorIdMap[userId]?.self &&
                commentType === 'UserMessage' && (
                  <div className={styles.dropdownContainer}>
                    <CommentDropdown
                      onShow={() => setDropdownMessageId(id)}
                      onEdit={() => {
                        setEditedText(comment);
                        setEditMessageId(id);
                      }}
                      onDelete={() => removeComment(id)}
                      positionClassName={dropdownPositionClassName}
                    />
                  </div>
                )}
            </div>
          ),
        )}
      </div>
      {tab === 'chat' && (
        <div className={styles.inputContainer}>
          <Textarea
            className={styles.textArea}
            onChange={e => setText(e.target.value)}
            onKeyDown={onTextKeyDown}
            maxLength={maxTextLength}
            placeholder={getText('comment-board-input-placeholder')}
            value={text}
          />
          <Button
            className={classNames(styles.button, styles.fullWidth)}
            disabled={text.trim().length === 0}
            onClick={postComment}
          >
            {getText('comment-board-post-button')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentBoard;
